import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Skeleton, Space, Typography } from 'antd';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    ModelBeliefsBrandHistory,
} from '../../../services/ai-admin-api';

const { Text } = Typography;

// The AI read's own trend, on its own surface.
//
// Deliberately NOT a series on the survey Trends chart (Steve 2026-09-27): the
// two share the -100..+100 scale, which is what makes them look commensurable
// while absolute calibration between them is still open — the launch
// comparison between AI and customers is rank order for exactly that reason.
// Here both series are AI measurements, so two axes are honest: Brand Equity is
// bipolar, consideration is a percentage.
const EQUITY_COLOUR = '#18216d';
const CONSIDERATION_COLOUR = '#ff825c';

interface Props {
    // Admin usage: fetch the brand's points. Customer usage passes `points`
    // straight from the read, which already carries them.
    brandId?: number;
    points?: ModelBeliefsBrandHistory['points'];
    refreshDays?: number;
    // The customer's v3 page: one line, in the reader's words. The v3 read has
    // no AI Brand Equity score on purpose, so that series and its axis would
    // contradict the page it sits on; "gate" and "instrument" are our words,
    // not the marketer's.
    customer?: boolean;
}

export const ModelBeliefsHistoryChart: React.FC<Props> = ({
    brandId,
    points: pointsProp,
    refreshDays: refreshDaysProp,
    customer = false,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';

    const [data, setData] = useState<ModelBeliefsBrandHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        // Nothing to fetch when the caller already has the points.
        if (!jwt || !brandId || pointsProp) return;
        setLoading(true);
        setError(null);
        try {
            setData(await aiAdminApi.getModelBeliefsBrandHistory(jwt, brandId));
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setLoading(false);
        }
    }, [jwt, brandId, pointsProp]);

    useEffect(() => {
        void load();
    }, [load]);

    // Two reads on one day are two points, so the label carries the time when
    // the dates collide — otherwise the axis shows the same day twice and the
    // chart looks duplicated rather than dense.
    const sameDay = useMemo(() => {
        const days = (pointsProp ?? data?.points ?? []).map((point) =>
            new Date(point.date).toDateString(),
        );
        return new Set(days).size !== days.length;
    }, [pointsProp, data]);

    // Points come from the caller (the customer read already carries them) or
    // from the admin fetch. One source of truth for both callers.
    const source = pointsProp ?? data?.points ?? [];
    const refreshDays = refreshDaysProp ?? data?.refreshDays ?? 30;

    // The customer chart plots one series, so a read with nothing on it (run
    // 27 asked one statement and no recommendation question) is not a point
    // and must not be counted, named in the note, or given an axis tick.
    const plotted = useMemo(
        () => (customer ? source.filter((point) => point.gateInclusionRate !== null) : source),
        [source, customer],
    );

    const rows = useMemo(
        () =>
            plotted.map((point) => ({
                date: new Date(point.date).toLocaleDateString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    // Four digits: "Sep 26, 26" read as a day and a year that
                    // happen to match, and nobody could tell which was which.
                    year: 'numeric',
                    ...(sameDay
                        ? ({ hour: 'numeric', minute: '2-digit' } as const)
                        : {}),
                }),
                models: point.models,
                panelSize: point.panelSize,
                aiBrandEquity: point.aiBrandEquity,
                // Stored as a fraction by the gate; a percentage is what the
                // axis and every other surface show.
                consideration:
                    point.gateInclusionRate === null
                        ? null
                        : Math.round(point.gateInclusionRate * 1000) / 10,
            })),
        [plotted, sameDay],
    );

    // Compared against the FULLEST read in the series, not against each run's
    // own panel: run 27 asked one model and recorded a panel of one, so by its
    // own snapshot it was complete. What makes it incomparable is that the read
    // beside it asked three.
    const fullestRead = useMemo(
        () => rows.reduce((most, row) => Math.max(most, row.models), 0),
        [rows],
    );
    // A smaller read is a point, not part of the trend: its value is split onto
    // its own key so the line never joins it to a fuller read. Drawing that
    // segment would assert a change between two numbers the instrument cannot
    // compare — on this brand, a 26-point "climb" in eleven hours that is
    // entirely one model against three.
    const chartRows = useMemo(
        () =>
            rows.map((row) => {
                const partial = row.models < fullestRead;
                return {
                    ...row,
                    partial,
                    equityFull: partial ? null : row.aiBrandEquity,
                    equityPartial: partial ? row.aiBrandEquity : null,
                    considerationFull: partial ? null : row.consideration,
                    considerationPartial: partial ? row.consideration : null,
                };
            }),
        [rows, fullestRead],
    );
    const partialReads = chartRows.filter((row) => row.partial);

    if (loading && !data && !pointsProp) return <Skeleton active paragraph={{ rows: 4 }} />;
    if (error) return <Alert type="error" showIcon message={error} />;

    if (rows.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No customer-triggered reads yet. Admin runs are excluded here: a calibration run is not a point in a brand's history."
            />
        );
    }

    return (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <ResponsiveContainer width="100%" height={260}>
                <LineChart
                    data={chartRows}
                    margin={{ top: 12, right: 16, left: 0, bottom: 8 }}
                >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                    {/* Padding so the first and last points sit inside the plot:
                        with the left axis hidden the first dot was drawn on
                        the edge and the hollow Sep 26 point vanished. */}
                    <XAxis dataKey="date" fontSize={12} padding={{ left: 24, right: 24 }} interval={0} />
                    {/* Both axes carry an id, so every series must name one:
                        an unlabelled series binds to a default axis that stops
                        existing the moment any axis is identified. */}
                    <YAxis
                        yAxisId="equity"
                        domain={[-100, 100]}
                        fontSize={12}
                        width={44}
                        hide={customer}
                    />
                    <YAxis
                        yAxisId="pct"
                        orientation="right"
                        domain={[0, 100]}
                        fontSize={12}
                        width={44}
                        tickFormatter={(value: number) => `${value}%`}
                    />
                    {/* 0 is neutral on the bipolar scale, not the floor. */}
                    {!customer && <ReferenceLine yAxisId="equity" y={0} stroke="#bbb" />}
                    <Tooltip
                        formatter={(value: any, name: string) =>
                            /Considered|Listed/.test(name)
                                ? [`${value}%`, name]
                                : [Number(value).toFixed(2), name]
                        }
                    />
                    {!customer && (
                        <Line
                            yAxisId="equity"
                            name="AI Brand Equity"
                            type="monotone"
                            dataKey="equityFull"
                            stroke={EQUITY_COLOUR}
                            strokeWidth={2}
                            dot={{ r: 4, fill: EQUITY_COLOUR, stroke: EQUITY_COLOUR }}
                            connectNulls
                        />
                    )}
                    {/* Smaller reads: hollow, and no line of their own. */}
                    {!customer && (
                        <Line
                            yAxisId="equity"
                            name="AI Brand Equity (smaller read)"
                            type="monotone"
                            dataKey="equityPartial"
                            stroke="none"
                            dot={{ r: 4, fill: '#fff', stroke: EQUITY_COLOUR, strokeWidth: 2 }}
                            legendType="none"
                        />
                    )}
                    <Line
                        yAxisId="pct"
                        name={customer ? 'Listed among six brands to consider' : 'Considered'}
                        type="monotone"
                        dataKey="considerationFull"
                        stroke={CONSIDERATION_COLOUR}
                        strokeWidth={2}
                        dot={{ r: 4, fill: CONSIDERATION_COLOUR, stroke: CONSIDERATION_COLOUR }}
                        connectNulls
                    />
                    <Line
                        yAxisId="pct"
                        name="Considered (smaller read)"
                        type="monotone"
                        dataKey="considerationPartial"
                        stroke="none"
                        dot={{ r: 4, fill: '#fff', stroke: CONSIDERATION_COLOUR, strokeWidth: 2 }}
                        legendType="none"
                    />
                </LineChart>
            </ResponsiveContainer>

            <Space size={16} wrap>
                {!customer && (
                    <Space size={6}>
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                background: EQUITY_COLOUR,
                                display: 'inline-block',
                            }}
                        />
                        <Text style={{ fontSize: 12 }}>AI Brand Equity (left, −100…+100)</Text>
                    </Space>
                )}
                <Space size={6}>
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            background: CONSIDERATION_COLOUR,
                            display: 'inline-block',
                        }}
                    />
                    <Text style={{ fontSize: 12 }}>
                        {customer
                            ? 'Share of answers with web search that listed you among six brands to consider'
                            : 'Considered in the gate (right, % of answers)'}
                    </Text>
                </Space>
            </Space>

            {partialReads.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={customer ? 'Not every read asked the same number of models' : 'Not every point is the same instrument'}
                    description={`${partialReads.length} of ${chartRows.length} reads asked fewer models than the fullest read here (${partialReads
                        .map(
                            (row) =>
                                `${row.date}: ${row.models} model${
                                    row.models === 1 ? '' : 's'
                                } against ${fullestRead}`,
                        )
                        .join(', ')}). They are drawn hollow and left unjoined. ${
                        customer
                            ? 'A step between a smaller read and a fuller one reflects how many models answered, not a change in what they believe.'
                            : 'A step between a smaller read and a fuller one is a change of instrument, not a change in what the models believe.'
                    }`}
                />
            )}

            {chartRows.length === 1 && (
                <Alert
                    type="info"
                    showIcon
                    message="One read so far"
                    description={`Movement needs two reads separated by time, not two reads: a second read the next day measures a record that has not moved. The refresh cadence is every ${
                        refreshDays
                    } days, so the second point arrives on its own.`}
                />
            )}
        </Space>
    );
};
